import React, { useEffect, useRef, useState } from 'react';
import {
  Order,
  ReturnStatus,
  ReturnTracking,
  WithdrawalReceipt,
} from '../utils/types';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  API_URL,
  formatPrice,
  isKnownShopId,
  loadStatusTranslations,
  STATUS_NS,
} from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import StatusProgressBar from './StatusProgressBar';
import { useStatusName } from '../utils/statusName';
import {
  statusPageKey,
  useStandaloneQr,
} from '../contexts/StandaloneQrContext';
import { shopIdentityOf } from '../services/orderLookupService';

const COPIED_MS = 2000;

// #149: the withdrawal a case C return continues (ReturnAndPaymentMethodPage)
interface WithdrawalOfReturn {
  id: number;
  receipt: WithdrawalReceipt;
  order: Order;
  proof: string | null;
  shop: string | null;
  standalone: boolean;
}

const canCopy = () =>
  typeof navigator !== 'undefined' &&
  !!navigator.clipboard &&
  typeof navigator.clipboard.writeText === 'function';

// #109 (S4, D9, r1-4, r2-3): the AWB row of the next-step card. Rendered with
// key={awb}, so an AWB change mounts a fresh row; the unmount cleanup clears
// the "Copied" timer and bumps the generation, so a clipboard promise that
// settles later neither announces nor shows "Copied". The live region is
// mounted with the row, before any click.
const AwbRow: React.FC<{ awb: string }> = ({ awb }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const generation = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const clearTimer = () => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  };

  useEffect(
    () => () => {
      generation.current += 1;
      clearTimer();
    },
    []
  );

  const copy = () => {
    const current = ++generation.current;
    clearTimer();
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(awb))
      .then(
        () => {
          if (current !== generation.current) return;
          setCopied(true);
          timer.current = window.setTimeout(() => {
            if (current === generation.current) setCopied(false);
          }, COPIED_MS);
        },
        () => {
          if (current === generation.current) setCopied(false);
        }
      );
  };

  return (
    <dl className="xc-kv">
      <dt>{t('tracking.awb')}</dt>
      <dd className="xc-next__awb">
        <span className="xc-next__awb-value">{awb}</span>
        {canCopy() && (
          <button type="button" className="xc-btn xc-btn--sm" onClick={copy}>
            {copied ? t('tracking.copied') : t('tracking.copy')}
          </button>
        )}
        <span className="xc-visually-hidden" aria-live="polite">
          {copied ? t('tracking.copied') : ''}
        </span>
      </dd>
    </dl>
  );
};

const ReturnPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'dynamic']);
  const statusName = useStatusName();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  // #105 P4-A: GET /returns/{id} now requires the signed sig (from the email / post-placement
  // redirect link). Read it from the URL and forward it; without a valid sig the API returns 403.
  const searchParams = new URLSearchParams(location.search);
  const sig = searchParams.get('sig');
  // Set by the create-return redirect when the backend response carried no tracking_sig (deploy
  // skew): the return WAS created, but we have no signed link to load it — show a distinct
  // "created but tracking unavailable" state instead of the generic load-error screen.
  const trackingLinkUnavailable =
    searchParams.get('tracking_link_unavailable') === '1';
  const navigate = useNavigate();
  // #42 / #101: set by step 2 (ReturnAndPaymentMethodPage) on the history entry it replaces right
  // after creating the return. The "created" heading and lead are one-shot: the flag is latched per
  // history entry and then consumed from that entry (replace, same URL), so a reload, a new
  // navigation (also to the same return) or Back to the consumed entry shows the neutral title, as
  // do the email / parcel QR permalink visits.
  // Documented limit: a bfcache restore of the same live page instance (browser Back to a frozen
  // page) may still show the created heading; no pageshow handling.
  const justCreated =
    (location.state as { justCreated?: boolean } | null)?.justCreated === true;
  // #149: a case C return carries its withdrawal (the complete continuation
  // context of the receipt page); kept in the entry across the consumption
  const withdrawal =
    (location.state as { withdrawal?: WithdrawalOfReturn } | null)
      ?.withdrawal ?? null;
  const [createdId, setCreatedId] = useState<string | null>(() =>
    justCreated && id ? id : null
  );
  // the key of the history entry whose justCreated flag was consumed by this component (StrictMode
  // replays effects; the replace is issued once per entry)
  const consumedKeyRef = useRef<string | null>(null);
  const [latchedKey, setLatchedKey] = useState(location.key);
  if (latchedKey !== location.key) {
    // a new history entry resets the latch (adjusting state during render, no stale frame), except
    // the entry produced by our own consumption replace of the latched entry, which keeps it
    setLatchedKey(location.key);
    if (consumedKeyRef.current !== latchedKey) {
      setCreatedId(justCreated && id ? id : null);
    }
  }
  const showCreated = createdId !== null && createdId === id;
  const [returnObject, setReturnObject] = useState<ReturnTracking | null>(null);
  const [statuses, setStatuses] = useState<ReturnStatus[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalReturnItem, setTotalReturnItem] = useState<number>(0);
  const [currentStatusOrder, setCurrentStatusOrder] = useState<number>(-1);
  // #69: the shop of the verified return; only this page uses it (never stored)
  const [verifiedShopId, setVerifiedShopId] = useState<number | null>(null);
  const [, setStatusLabelsLoads] = useState<number>(0);
  // #125: the header shows the shop of this signed return (App keys it by
  // `<id>|<sig>`); cleared on unmount
  const { setHeaderShop, active: standaloneQr } = useStandaloneQr();
  useEffect(() => () => setHeaderShop(null), [setHeaderShop]);
  // #101 (r1 bot): consume the flag only once the created heading is on screen: at once on the
  // no-signature fallback, after a successful load on the signed path. A failed load keeps the
  // flag, so the Retry reload of this entry still shows the heading once it succeeds.
  const fallbackView = trackingLinkUnavailable && !sig;
  const headingRendered =
    fallbackView || (!loading && !error && returnObject !== null);
  useEffect(() => {
    if (
      !justCreated ||
      !id ||
      !headingRendered ||
      consumedKeyRef.current === location.key
    )
      return;
    consumedKeyRef.current = location.key;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: withdrawal ? { withdrawal } : null }
    );
  }, [
    withdrawal,
    justCreated,
    id,
    headingRendered,
    navigate,
    location.key,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const currentItems = returnObject?.return_products || [];

  // Move sortedStatuses inside useMemo or calculate it when rendering
  const sortedStatuses = React.useMemo(
    () => [...statuses].sort((a, b) => a.order_index - b.order_index),
    [statuses]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchReturnStatuses = async (sigQuery: string) => {
      if (cancelled) return;

      try {
        // /returns/{id}/flow is keyed by the path id and, since #69, requires the same signed sig
        // as the tracking endpoint (no Shop-Id header on the backend).
        const response = await fetch(
          API_URL + `/returns/${id}/flow${sigQuery}`,
          {
            method: 'GET',
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }

        const ret: ReturnStatus[] = await response.json();

        if (!cancelled) {
          setStatuses(ret);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'An error occurred');
        }
      }
    };

    const fetchReturn = async () => {
      // #69: a new tracking identity (id / sig) starts unverified
      setVerifiedShopId(null);
      // #125: so does the header shop, at the start of every load (and on the
      // no-signature fallback, which loads nothing)
      setHeaderShop(null);
      if (!id) return;
      // Created-but-no-sig fallback: nothing to fetch (the load would 403), render the dedicated
      // "tracking link unavailable" state instead.
      if (trackingLinkUnavailable && !sig) {
        if (!cancelled) {
          setError(null);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const sigQuery = sig ? `?sig=${encodeURIComponent(sig)}` : '';
        const response = await fetch(API_URL + `/returns/${id}${sigQuery}`, {
          method: 'GET',
        });

        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }

        const ret: ReturnTracking = await response.json();

        if (!cancelled) {
          setReturnObject(ret);
          setCurrentStatusOrder(ret.return_status.order_index);

          const uniqueIds = new Set(
            ret.return_products.map(product => product.line_item.id)
          );
          setTotalReturnItem(uniqueIds.size);

          // #69: the labels belong to the shop of the verified return, never to the shop a
          // reused browser remembers. Without a verified shop (older return-api) no shop labels
          // are loaded and the neutral status names are shown.
          setVerifiedShopId(
            isKnownShopId(Number(ret.shop_id)) ? Number(ret.shop_id) : null
          );

          // #125: the header shop of this signed link (shop_name /
          // shop_domain); an older return-api or an invalid identity leaves
          // the caption only
          const headerKey = statusPageKey(id, sig);
          const shop = shopIdentityOf({
            id: ret.shop_id,
            domain: ret.shop_domain,
            name: ret.shop_name,
          });
          if (headerKey !== null && shop !== null) {
            setHeaderShop({ key: headerKey, shop });
          }

          // Fetch statuses after setting return object
          await fetchReturnStatuses(sigQuery);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'An error occurred');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReturn();

    return () => {
      cancelled = true;
    };
  }, [id, sig, trackingLinkUnavailable, setHeaderShop]); // re-fetch if id, signature, or fallback mode changes

  // #69: (re)load the verified shop's status labels for the current language
  useEffect(() => {
    if (verifiedShopId === null) return;
    let cancelled = false;
    loadStatusTranslations(verifiedShopId, i18n.language).then(() => {
      if (!cancelled) setStatusLabelsLoads(n => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [verifiedShopId, i18n.language]);

  // #109 (S4, D4 / D9): one title row; the #101 created state is the
  // Created chip on the row and the lead under it (no heading swap)
  const titleRow = (title: string, orderChip: string | null) => (
    <div className="xc-title-row">
      <h2 className="xc-title">{title}</h2>
      {(orderChip !== null || showCreated) && (
        <span className="xc-title-row__chips">
          {orderChip !== null && (
            <span className="xc-chip" title={orderChip}>
              {orderChip}
            </span>
          )}
          {showCreated && (
            <span className="xc-chip xc-chip--ok">{t('tracking.created')}</span>
          )}
        </span>
      )}
    </div>
  );
  const lead = showCreated ? (
    <p className="xc-intro">{t('return.initiated')}</p>
  ) : null;

  if (trackingLinkUnavailable && !sig) {
    // r1-11: the route id is not the display number, so the title is the
    // neutral "Your return" and the id stays in the notice
    return (
      <div className="xc-screen">
        {titleRow(t('return.statusTitle'), null)}
        {lead}
        <div className="xc-notice xc-notice--warn" role="alert">
          {t('return.trackingLinkUnavailable', { id })}
        </div>
      </div>
    );
  }

  if (loading && !error) {
    return (
      <div className="xc-screen">
        <div className="xc-status" role="status">
          <span className="xc-spinner" aria-hidden="true"></span>
          {t('tracking.loading')}
        </div>
      </div>
    );
  }

  if (error || !returnObject) {
    return (
      <div className="xc-screen">
        <div className="xc-notice xc-notice--error" role="alert">
          <p className="xc-notice__text">{t('return.loadingError')}</p>
          <button
            type="button"
            className="xc-btn xc-btn--sm"
            onClick={() => window.location.reload()}
          >
            {t('buttons.retry')}
          </button>
        </div>
      </div>
    );
  }

  // #69: the shop label of a status when the verified shop's labels are
  // loaded, else the neutral text of the flow (the same rule as the timeline)
  const shopLabels = verifiedShopId !== null;
  const statusText = (
    status: ReturnStatus,
    field: 'name' | 'description'
  ): string =>
    shopLabels
      ? String(
          t(`${STATUS_NS}:return_status_${status.id}.${field}`, {
            defaultValue: status[field] ?? '',
          }) ?? ''
        )
      : (status[field] ?? '');
  // #128: the reason of a returned product, by the same rule
  const reasonText = (reason: { id: number; display_name: string }): string =>
    shopLabels
      ? String(
          t(`${STATUS_NS}:return_reason_${reason.id}.display_name`, {
            defaultValue: reason.display_name ?? '',
          }) ?? ''
        )
      : (reason.display_name ?? '');
  const currentStatus = sortedStatuses.find(
    s => s.order_index === currentStatusOrder
  );
  const nextTitle = currentStatus ? statusName(currentStatus, shopLabels) : '';
  const nextText = currentStatus
    ? statusText(currentStatus, 'description')
    : '';
  const awb = returnObject.awb;
  // #137: the locker (easybox) drop-off code, shown under the AWB; trimmed,
  // otherwise as received (no case change), nothing for null / blank / absent.
  const lockerCode = returnObject.locker_code?.trim() || null;
  const orderChip = `${t('orderDetails.order')} ${returnObject.order_name}`;
  const deliveryPrice = returnObject.return_method_price;

  return (
    <div className="xc-screen">
      {titleRow(
        t('tracking.title', { number: returnObject.number }),
        orderChip
      )}
      {lead}

      {/* timeline (D9, r1-6): none for an empty flow */}
      {sortedStatuses.length > 0 && (
        <div className="xc-card">
          {sortedStatuses.length === 1 ? (
            <p className="xc-tl__single">
              {t('tracking.current', {
                name: statusName(sortedStatuses[0], false),
              })}
            </p>
          ) : (
            <StatusProgressBar
              shopLabels={shopLabels}
              statuses={statuses}
              currentOrder={currentStatusOrder}
            />
          )}
        </div>
      )}

      {/* next step (D9, r1-5): only from the tracking object and the flow */}
      {(currentStatus || awb || lockerCode) && (
        <div className="xc-card">
          <h3 className="xc-card__title">{t('tracking.next')}</h3>
          <div className="xc-next">
            <span className="xc-next__icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                focusable="false"
              >
                <rect x="3" y="7" width="13" height="10" rx="2" />
                <path d="M16 10h3l2 3v4h-5M7 20a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17 20a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
            </span>
            <div className="xc-next__body">
              {currentStatus && <p className="xc-next__title">{nextTitle}</p>}
              {currentStatus && nextText !== '' && (
                <p className="xc-next__text">{nextText}</p>
              )}
              {awb && <AwbRow key={awb} awb={awb} />}
              {lockerCode && (
                <dl className="xc-kv">
                  <dt>{t('tracking.lockerCode')}</dt>
                  <dd className="xc-next__locker">
                    <span className="xc-next__awb-value">{lockerCode}</span>
                  </dd>
                </dl>
              )}
              {/* #181: how to drop the parcel at an easybox with that code */}
              {lockerCode && (
                <p className="xc-next__text xc-next__locker-help">
                  {t('tracking.lockerHelp')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* returned products and totals */}
      <div className="xc-card">
        <h3 className="xc-card__title xc-card__title--split">
          <span>{t('return.products')}</span>
          <span className="xc-card__count">
            {t('returnWithCount', { count: totalReturnItem })}
          </span>
        </h3>
        {currentItems.map((item, index) => {
          const exchange = item.exchange_product;
          return (
            <div
              key={`${item.line_item.id}-${item.return_reason.id}-${index}`}
              className="xc-prod"
            >
              {item.line_item.image_src ? (
                <div className="xc-prod__img">
                  <img
                    src={item.line_item.image_src}
                    alt={item.line_item.product_title}
                  />
                </div>
              ) : (
                <div className="xc-prod__img xc-prod__img--empty">
                  <span className="xc-visually-hidden">
                    {t('products.noImage')}
                  </span>
                </div>
              )}
              <div>
                <p className="xc-prod__name">
                  {item.quantity} {'\u00d7'} {item.line_item.product_title}
                </p>
                <p className="xc-prod__meta">
                  {item.line_item.variant_title}
                  {item.line_item.variant_title ? ` ${'\u00b7'} ` : ''}
                  <span className="xc-prod__price">
                    {formatPrice(item.price, item.currency)}
                  </span>
                </p>
                <div className="xc-prod__chips">
                  <span className="xc-chip xc-chip--wrap">
                    <span className="xc-visually-hidden">
                      {t('return.reason')}:{' '}
                    </span>
                    {reasonText(item.return_reason)}
                  </span>
                  {exchange && (
                    <span className="xc-chip xc-chip--accent xc-chip--wrap">
                      {t('productDetails.exchangeWith')}: {item.quantity}{' '}
                      {'\u00d7'} {exchange.product_title}
                      {exchange.variant_title !== 'Default Title'
                        ? ` - ${exchange.variant_title}`
                        : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div className="xc-divider" />
        <div className="xc-total">
          {deliveryPrice !== null &&
            deliveryPrice !== undefined &&
            deliveryPrice >= 0 && (
              <div className="xc-total__row">
                <span>{t('return.delivery')}</span>
                <span>
                  {deliveryPrice > 0 ? '- ' : ''}
                  {formatPrice(deliveryPrice, returnObject.currency)}
                </span>
              </div>
            )}
          <div className="xc-total__row xc-total__row--big">
            <span>{t('return.total')}</span>
            <span>
              {formatPrice(returnObject.price, returnObject.currency)}
            </span>
          </div>
          <p className="xc-total__note">{t('return.amount')}</p>
        </div>
      </div>

      {/* #149: the withdrawal statement of a case C return. A full-screen QR
          session ended here (no receipt reachable): the confirmation e-mail
          is the durable copy */}
      {withdrawal &&
        (standaloneQr || withdrawal.standalone ? (
          <div className="xc-card xc-card--quiet">
            <p className="xc-help">{t('withdrawal.receiptEmailNote')}</p>
          </div>
        ) : (
          <div className="xc-card xc-card--quiet">
            <Link
              to={'/withdrawal/' + withdrawal.id}
              state={{
                receipt: withdrawal.receipt,
                order: withdrawal.order,
                proof: withdrawal.proof,
                shop: withdrawal.shop,
                standalone: withdrawal.standalone,
              }}
              className="xc-btn xc-btn--sm"
            >
              {t('withdrawal.receiptLink')}
            </Link>
          </div>
        ))}

      {/* help (mockup screen 4, quiet card) */}
      <div className="xc-card xc-card--quiet">
        <h3 className="xc-card__title">{t('tracking.help')}</h3>
        <p className="xc-help">{t('tracking.helpText')}</p>
      </div>
    </div>
  );
};

export default ReturnPage;
